import { useCallback, useEffect, useRef, useState } from "react";

import { fetchEventSource } from "@microsoft/fetch-event-source";
import axios from "axios";
import { CLIENT_SYNC_SVC_ADDRESS } from "constants/app.constants";

import { apiRequestHeadersFor } from "api/apiRequestHeaders";

// ─── Sentinel error ───────────────────────────────────────────────────────────
// Thrown inside fetchEventSource's `onerror` to stop the library from retrying
// without surfacing a generic rethrow as an unhandled rejection.

class FatalPerfSseError extends Error {
  constructor(cause?: unknown) {
    super(
      cause instanceof Error
        ? cause.message
        : "Performance calculation SSE stream closed with an error."
    );
    this.name = "FatalPerfSseError";
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * The subset of sync types that can trigger a performance calculation job.
 * `columns` and `sticks` do not affect performance metrics and must never be
 * passed to this hook — the narrowed type makes that constraint explicit at
 * compile time.
 */
export type PerformanceTriggerSyncType = "production" | "forecast";

export interface PerfCalcAcceptedResponse {
  readonly correlationId: string;
}

export interface PerfCalcCompletePayload {
  readonly correlationId?: string;
  readonly jobId?: string;
  readonly jobName?: string;
  readonly status?: "completed";
  readonly timestamp?: string;
}

export interface PerfCalcErrorPayload {
  readonly correlationId?: string;
  readonly jobId?: string;
  readonly jobName?: string;
  readonly status?: "failed";
  readonly failedReason?: string;
  readonly message?: string;
  readonly timestamp?: string;
}

export interface UsePerformanceCalculationProps {
  readonly jwtToken?: string;
  /**
   * Called when a performance calculation job settles successfully.
   *
   * @param triggeringSyncTypes - The sync types that originally triggered this
   *   job (always a single-element array in the current flow, but typed as
   *   a readonly array for forward compatibility).
   * @param timestamp - ISO-8601 settlement timestamp from the `perf-calc-complete`
   *   payload, or a local `Date.now()` fallback if the payload lacks one.
   */
  readonly onComplete: (
    triggeringSyncTypes: readonly PerformanceTriggerSyncType[],
    schema: string,
    timestamp: string
  ) => void;
  /**
   * Called when a performance calculation job fails at any stage:
   * - `POST /sync/{schema}/performance` returns a non-2xx response or times out
   * - The SSE stream delivers a `perf-calc-error` event
   * - The SSE connection closes unexpectedly
   *
   * A failure here does NOT affect the status of the underlying data sync.
   */
  readonly onError: (
    triggeringSyncTypes: readonly PerformanceTriggerSyncType[],
    schema: string,
    errorMessage: string
  ) => void;
}

export interface UsePerformanceCalculationReturn {
  /**
   * `true` while at least one performance calculation job has been enqueued
   * and is awaiting its terminal SSE event. Becomes `false` once every pending
   * job settles (success or failure).
   */
  readonly isCalculating: boolean;
  /**
   * Triggers a performance calculation job for the given schema and sync type.
   *
   * Calling this fires `POST /sync/{schema}/performance`, then opens an SSE
   * stream on the returned `correlationId` to observe the terminal
   * `perf-calc-complete` or `perf-calc-error` event. Multiple concurrent calls
   * (e.g. from a super-sync that settles both production and forecast) are
   * handled independently.
   */
  readonly triggerForSyncType: (
    schema: string,
    syncType: PerformanceTriggerSyncType
  ) => void;
  /**
   * Aborts and clears every in-flight performance job immediately.
   *
   * This is intended for lifecycle boundaries such as schema changes or
   * component teardown, where stale jobs must stop driving loading state for
   * the newly active schema.
   */
  readonly reset: () => void;
}

// ─── API helpers ──────────────────────────────────────────────────────────────

const buildPerformanceTriggerPath = (schema: string): string =>
  `${CLIENT_SYNC_SVC_ADDRESS}/sync/${encodeURIComponent(schema)}/performance`;

const buildPerfCalcStreamPath = (correlationId: string): string =>
  `${CLIENT_SYNC_SVC_ADDRESS}/sync/${encodeURIComponent(correlationId)}/events`;

const postTriggerPerformance = async (
  schema: string
): Promise<PerfCalcAcceptedResponse> => {
  const response = await axios.post<PerfCalcAcceptedResponse>(
    buildPerformanceTriggerPath(schema)
  );
  return response.data;
};

const getPerformanceErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data;
    if (
      data &&
      typeof data === "object" &&
      "message" in data &&
      typeof (data as { message?: unknown }).message === "string"
    ) {
      return (data as { message: string }).message;
    }
    return error.message ?? "Performance calculation enqueue failed.";
  }

  if (error instanceof Error) {
    return error.message || "Performance calculation enqueue failed.";
  }

  return "Performance calculation enqueue failed.";
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Manages the full lifecycle of server-side performance calculation jobs.
 *
 * After a successful data sync, the caller triggers a job via
 * `triggerForSyncType`. The hook POSTs to `sync-svc` to enqueue the job,
 * opens an SSE stream to observe it, and notifies the caller via `onComplete`
 * or `onError` when the job settles.
 *
 * Multiple concurrent jobs (e.g. production + forecast from a super-sync) are
 * tracked independently. `isCalculating` remains `true` until every pending
 * job has settled.
 *
 * @example
 * const { isCalculating, triggerForSyncType } = usePerformanceCalculation({
 *   jwtToken,
 *   onComplete: (syncTypes, timestamp) => {
 *     setPerformanceStatus({ lastUpdated: timestamp });
 *   },
 *   onError: (syncTypes, message) => {
 *     showError(message);
 *   },
 * });
 */
export const usePerformanceCalculation = ({
  jwtToken,
  onComplete,
  onError
}: UsePerformanceCalculationProps): UsePerformanceCalculationReturn => {
  // ── Reactive state ─────────────────────────────────────────────────────────
  // Only the set of pending correlation IDs needs to be reactive — its `.size`
  // drives `isCalculating`. All other tracking (controllers, syncType mapping)
  // lives in refs to avoid unnecessary re-renders.

  const [pendingCorrelationIds, setPendingCorrelationIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  // ── Non-reactive refs ──────────────────────────────────────────────────────

  // AbortControllers keyed by correlationId for stream teardown.
  const activeStreamControllersRef = useRef<Map<string, AbortController>>(new Map());

  // Maps correlationId → the syncType that triggered it so callbacks receive
  // the correct attribution.
  const pendingJobSyncTypesRef = useRef<Map<string, PerformanceTriggerSyncType>>(
    new Map()
  );

  // Maps syncType → the most recently registered correlationId for that type.
  // Used to detect and abort a hung previous job when the user re-triggers the
  // same syncType before its SSE stream has delivered a terminal event.
  const activeSyncTypeJobRef = useRef<Map<PerformanceTriggerSyncType, string>>(new Map());

  // ── Stable callback refs ───────────────────────────────────────────────────
  // Storing the parent callbacks in refs means `triggerForSyncType` never needs
  // to close over a fresh callback reference — it always reads the latest
  // version without requiring a dependency-driven recreation.

  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  // ── Job settlement ─────────────────────────────────────────────────────────

  const settleJob = useCallback((correlationId: string) => {
    const controller = activeStreamControllersRef.current.get(correlationId);
    controller?.abort();
    activeStreamControllersRef.current.delete(correlationId);

    const syncType = pendingJobSyncTypesRef.current.get(correlationId);
    pendingJobSyncTypesRef.current.delete(correlationId);

    // Only evict the syncType entry if it still points to THIS correlationId.
    // If a newer job has already replaced it, leave the newer entry alone.
    if (
      syncType !== undefined &&
      activeSyncTypeJobRef.current.get(syncType) === correlationId
    ) {
      activeSyncTypeJobRef.current.delete(syncType);
    }

    setPendingCorrelationIds((prev) => {
      const next = new Set(prev);
      next.delete(correlationId);
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    for (const controller of activeStreamControllersRef.current.values()) {
      controller.abort();
    }

    activeStreamControllersRef.current.clear();
    pendingJobSyncTypesRef.current.clear();
    activeSyncTypeJobRef.current.clear();
    setPendingCorrelationIds(new Set());
  }, []);

  // ── SSE stream management ──────────────────────────────────────────────────

  const openPerfCalcStream = useCallback(
    (correlationId: string, syncType: PerformanceTriggerSyncType, schema: string) => {
      // Defensively abort any stale stream that might already exist for this
      // correlationId before opening a new one.
      activeStreamControllersRef.current.get(correlationId)?.abort();

      const controller = new AbortController();
      activeStreamControllersRef.current.set(correlationId, controller);

      const streamUrl = buildPerfCalcStreamPath(correlationId);
      void fetchEventSource(streamUrl, {
        signal: controller.signal,
        headers: {
          ...apiRequestHeadersFor(streamUrl),
          ...(jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {})
        },

        onmessage(event) {
          // Drop events that arrive after the stream has been torn down (e.g.
          // on unmount). The real fetchEventSource library respects the abort
          // signal, but this guard adds defence-in-depth for environments where
          // the signal check is bypassed (e.g. direct handler invocation in tests).
          if (controller.signal.aborted) return;

          if (event.event === "perf-calc-complete") {
            try {
              const payload = JSON.parse(event.data) as PerfCalcCompletePayload;
              settleJob(correlationId);
              onCompleteRef.current(
                [syncType],
                schema,
                payload.timestamp ?? new Date().toISOString()
              );
            } catch {
              settleJob(correlationId);
              onCompleteRef.current([syncType], schema, new Date().toISOString());
            }
          } else if (event.event === "perf-calc-error") {
            try {
              const payload = JSON.parse(event.data) as PerfCalcErrorPayload;
              settleJob(correlationId);
              onErrorRef.current(
                [syncType],
                schema,
                payload.failedReason ??
                  payload.message ??
                  "Performance calculation failed."
              );
            } catch {
              settleJob(correlationId);
              onErrorRef.current([syncType], schema, "Performance calculation failed.");
            }
          }
        },

        onerror(error) {
          // settleJob is synchronous (setState only enqueues an update, it
          // does not block), so the throw still happens in the same stack frame.
          settleJob(correlationId);

          onErrorRef.current(
            [syncType],
            schema,
            "Performance calculation stream closed unexpectedly."
          );

          // Throwing stops fetchEventSource from scheduling any retry attempts.
          throw new FatalPerfSseError(error);
        }
      });
    },
    [jwtToken, settleJob]
  );

  // ── Public trigger ─────────────────────────────────────────────────────────

  const triggerForSyncType = useCallback(
    (schema: string, syncType: PerformanceTriggerSyncType) => {
      void (async () => {
        try {
          const { correlationId } = await postTriggerPerformance(schema);

          // If a previous job for this syncType is still pending (e.g. its SSE
          // stream hung without ever delivering a terminal event), silently
          // abort it so only the newest request drives the loading indicator.
          const previousCorrelationId = activeSyncTypeJobRef.current.get(syncType);
          if (previousCorrelationId !== undefined) {
            settleJob(previousCorrelationId);
          }

          // Register the new job as the authoritative one for this syncType.
          activeSyncTypeJobRef.current.set(syncType, correlationId);
          pendingJobSyncTypesRef.current.set(correlationId, syncType);
          setPendingCorrelationIds((prev) => new Set([...prev, correlationId]));

          openPerfCalcStream(correlationId, syncType, schema);
        } catch (error) {
          // Enqueue failed — ERR-16. The underlying sync data is unaffected.
          // No SSE stream is opened; notify the caller immediately.
          onErrorRef.current([syncType], schema, getPerformanceErrorMessage(error));
        }
      })();
    },
    [openPerfCalcStream, settleJob]
  );

  // ── Cleanup on unmount ─────────────────────────────────────────────────────

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  // ── Public interface ───────────────────────────────────────────────────────

  return {
    isCalculating: pendingCorrelationIds.size > 0,
    triggerForSyncType,
    reset
  };
};
